// Single source of truth for the services shown on the landing page
// (ServicesLanding.vue) and on the detail page (pages/services/index.vue).
// Add or edit a service here and it updates in both places.

export interface ServiceCapability {
  title: string;
  description: string;
}

export interface Service {
  slug: string;
  name: string;
  image: string;
  // Short line used on the landing icons / intros
  tagline: string;
  // One or two paragraphs of overview
  intro: string[];
  // The "what we do" blocks
  capabilities: ServiceCapability[];
  // Concrete things the client walks away with
  deliverables: string[];
}

export const services: Service[] = [
  {
    slug: 'web-development',
    name: 'Web Development',
    image: '/assets/services/web_development_v2.webp',
    tagline: 'Fast, scalable web apps and sites built to last.',
    intro: [
      'We build production-grade web applications and marketing sites from the ground up — clean architecture, typed codebases and performance that holds up as you grow.',
      'Whether it is a high-traffic platform, an internal tool or a marketing site that needs to convert, we ship code that is maintainable long after launch.',
    ],
    capabilities: [
      {
        title: 'Front-end engineering',
        description:
          'Component-driven interfaces in Vue, Nuxt and React — accessible, responsive and tuned for Core Web Vitals.',
      },
      {
        title: 'Back-end & APIs',
        description:
          'Robust services and APIs in Go, Node and Laravel, with databases and integrations designed to scale.',
      },
      {
        title: 'Performance & SEO',
        description:
          'Server-side rendering, smart caching and a lean asset pipeline so pages load fast and rank well.',
      },
    ],
    deliverables: [
      'Responsive, accessible UI',
      'Typed, documented codebase',
      'CI/CD and deployment setup',
      'Analytics and SEO foundations',
    ],
  },
  {
    slug: 'ui-ux-design',
    name: 'UI/UX Design',
    image: '/assets/services/UIUX_design_v2.webp',
    tagline: 'Interfaces that feel obvious and look effortless.',
    intro: [
      'We design products people understand instantly. Starting from real user needs, we move from flows and wireframes to polished, on-brand interfaces.',
      'Every screen is built in a design system so the look stays consistent and the hand-off to engineering is seamless.',
    ],
    capabilities: [
      {
        title: 'Research & flows',
        description:
          'We map the journeys that matter, pressure-test them early and remove friction before a single pixel is pushed.',
      },
      {
        title: 'Interface design',
        description:
          'High-fidelity, responsive screens in Figma with a reusable component library and clear visual hierarchy.',
      },
      {
        title: 'Prototyping',
        description:
          'Interactive prototypes so you can feel the product and validate decisions before they hit development.',
      },
    ],
    deliverables: [
      'User flows and wireframes',
      'Figma design system',
      'High-fidelity screens',
      'Interactive prototype',
    ],
  },
  {
    slug: 'support-maintenance',
    name: 'Support & Maintenance',
    image: '/assets/services/maintenance_v2.webp',
    tagline: 'We keep what you ship healthy, secure and fast.',
    intro: [
      'Launch is the start, not the finish. We monitor, patch and improve your product so it stays reliable as usage and dependencies change.',
      'From urgent fixes to steady iteration, you get a team that already knows your codebase and responds quickly.',
    ],
    capabilities: [
      {
        title: 'Monitoring & uptime',
        description:
          'Proactive monitoring, alerting and quick response so issues are caught before your users feel them.',
      },
      {
        title: 'Updates & security',
        description:
          'Regular dependency updates, security patches and performance tuning to keep everything current.',
      },
      {
        title: 'Ongoing iteration',
        description:
          'A predictable cadence of improvements and new features, prioritised around what moves your metrics.',
      },
    ],
    deliverables: [
      'Monitoring and alerting',
      'Scheduled updates and patches',
      'Performance reviews',
      'Priority support response',
    ],
  },
  {
    slug: 'product-development',
    name: 'Product Development',
    image: '/assets/services/product_development_v2.webp',
    tagline: 'From idea to launched product — end to end.',
    intro: [
      'We partner with you from the first whiteboard sketch to a live product, owning strategy, design and engineering as one team.',
      'We ship a focused MVP fast, learn from real usage and iterate towards product-market fit instead of guessing.',
    ],
    capabilities: [
      {
        title: 'Strategy & scoping',
        description:
          'We turn an idea into a clear roadmap — defining the core problem, the MVP and the path to launch.',
      },
      {
        title: 'Design & build',
        description:
          'Design and engineering working in lockstep so the product is cohesive, fast to build and easy to evolve.',
      },
      {
        title: 'Launch & iterate',
        description:
          'We get it in front of real users, measure what matters and refine the product release after release.',
      },
    ],
    deliverables: [
      'Product roadmap',
      'MVP design and build',
      'Launch-ready application',
      'Iteration and growth plan',
    ],
  },
];

export const getServiceBySlug = (slug: string): Service | undefined =>
  services.find((service) => service.slug === slug);
